import { CardSection } from "~/components/atoms/CardSection";
import avatar from "~/assets/avatar-circle.svg";
import star from "~/assets/star-circle.svg";
import mail from "~/assets/mail-circle.svg";
import { JoinButton } from "~/components/molecules/JoinButton";
import { CoachResult } from "~/lib/api/ecosystemV2.server";
import Image from "~/components/atoms/Image";
import { CardSectionRow } from "~/components/atoms/CardSectionRow";
import {
    Modal,
    ModalContent,
    ModalDescription,
    ModalHeader,
    ModalTitle,
    ModalTrigger,
} from "~/components/molecules/Modal";
import iconCopy from "~/assets/icon-copy.svg";
import { toast } from "~/components/molecules/Toast";
import React, { ReactNode } from "react";
import { RouteIds, useRouteData } from "~/lib/routing";
import { useLocation } from "react-router";
import { Skeleton } from "~/components/atoms/Skeleton";

function Step({
    icon: { src, alt },
    title,
    subtitle,
}: {
    icon: { src: string; alt: string };
    title: string;
    subtitle: string;
}) {
    return (
        <div className="flex flex-col items-center gap-2 px-6 text-center">
            <Image src={src} alt={alt} className="size-[40px]" />
            <p className="font-body-bold">{title}</p>
            <p className="font-body">{subtitle}</p>
        </div>
    );
}

interface Props {
    name: string;
    coaches?: CoachResult[];
    authenticated: boolean;
}

const positions = [
    /^Athletic Director/gi,
    /^Head Coach/gi,
    /Associate Athletic Director/gi,
    /Assistant Athletic Director/gi,
    /Associate Head Coach/gi,
    /Assistant Head Coach/gi,
    /Assistant Coach/gi,
    /^Director/gi,
    /Assistant Director/gi,
];

function findRegExpIndex(regexes: RegExp[], token: string): number {
    return regexes.findIndex(r => {
        const matches = token.match(r);
        return matches && matches.length > 0;
    });
}

export function orderByPosition(a: CoachResult, b: CoachResult): number {
    const i = findRegExpIndex(positions, a.attrs.listed_position);
    const j = findRegExpIndex(positions, b.attrs.listed_position);
    if (i === j) return a.attrs.listed_position.localeCompare(b.attrs.listed_position);
    if (i === -1) return 1;
    if (j === -1) return -1;
    return i - j;
}

async function copyEmail(email: string) {
    await navigator.clipboard.writeText(email).then(() => {
        toast({
            type: "success",
            content: "Email address copied",
            closeButton: true,
        });
    });
}

export function BlurContent({ children }: { children: ReactNode }) {
    return (
        <div className="pointer-events-none flex select-none flex-col items-end gap-4 rounded-[10px] bg-blue-20/60 p-2 blur-sm">
            {children}
        </div>
    );
}

function CoachLeft(coach: CoachResult) {
    return coach.attrs.listed_position;
}

function CoachRight(coach: CoachResult) {
    return <div className="font-subheader-1 min-w-fit text-right">{coach.attrs.name}</div>;
}

function BetaCoachRight(coach: CoachResult, withBlur: boolean) {
    if (withBlur) {
        return (
            <BlurContent>
                <div className="font-subheader-1 min-w-fit text-right">Lorem ipsum dolor</div>
                <div className="text-blue-80">Lorem.ipsum@email.com</div>
            </BlurContent>
        );
    }

    return CoachRight(coach);
}

function subContentEmail(c: CoachResult, withBlur: boolean) {
    return withBlur ? null : (
        <div
            className="mb-6 flex w-full cursor-pointer justify-end text-right text-blue-80"
            onClick={() => {
                void copyEmail(c.attrs.email as string);
            }}
        >
            {c.attrs.email} <Image alt="copy-icon" src={iconCopy} className="pl-2"></Image>
        </div>
    );
}

function Table({
    coaches,
    withBlur,
    isBeta,
}: {
    coaches: CoachResult[];
    withBlur: boolean;
    isBeta: boolean;
}) {
    if (isBeta) {
        const table = coaches.map(c => (
            <div key={c.id}>
                <CardSectionRow
                    key={c.id}
                    left={CoachLeft(c)}
                    right={BetaCoachRight(c, withBlur)}
                    subContent={subContentEmail(c, withBlur)}
                />
            </div>
        ));
        return <div className="max-h-full overflow-y-auto">{table}</div>;
    }
    return coaches.map(c => (
        <div key={c.id}>
            <CardSectionRow key={c.id} left={CoachLeft(c)} right={CoachRight(c)} />
        </div>
    ));
}

function LeadFormCTA({ name, returnTo }: Pick<Props, "name"> & { returnTo: string }) {
    return (
        <div className="flex flex-col items-center gap-8 bg-blue-20 p-6 sm:rounded">
            <p className="font-subheader-1 sm:font-header-xs text-center text-blue-80 sm:uppercase">
                {`Interested in ${name}?`}
                <br />
                Let the Coaching Staff know in 3 simple steps:
            </p>
            <div className="grid w-full grid-flow-row gap-8 sm:grid-flow-col">
                <Step
                    icon={{ src: avatar, alt: "person" }}
                    title="1. Create a Profile with NCSA"
                    subtitle="Coaches want to know who you are!"
                />
                <Step
                    icon={{ src: star, alt: "star" }}
                    title={`2. Favorite ${name}`}
                    subtitle="Coaches can see when you favorite!"
                />
                <Step
                    icon={{ src: mail, alt: "envelope" }}
                    title="3. Introduce yourself"
                    subtitle="Not sure what to say? We can help!"
                />
            </div>
            <JoinButton size="lg" data-testid="program-staff" returnTo={returnTo} />
        </div>
    );
}

function BetaLeadFormCTA({
    authenticated,
    returnTo,
}: {
    authenticated: boolean;
    returnTo: string;
}) {
    if (authenticated) {
        return null;
    }
    return (
        <div className="flex flex-col items-center gap-8 rounded bg-blue-20 p-6">
            <p className="font-subheader-1 max-w-[390px] text-center text-blue-80">
                Get access to coaches' names and contact info when you create a free account.
            </p>
            <JoinButton size="lg" data-testid="program-staff" returnTo={returnTo} />
        </div>
    );
}

const ProgramStaffContainer = ({
    children,
    title,
}: { title: string } & React.PropsWithChildren) => (
    <CardSection title={title}>
        <div className="space-y-4 px-6" data-testid="coaching-staff-section">
            {children}
        </div>
    </CardSection>
);

export const ProgramStaffLoading = () => (
    <ProgramStaffContainer title="Coaching Staff">
        <Skeleton className="h-[340px]" />
    </ProgramStaffContainer>
);

export const ProgramStaffError = () => (
    <ProgramStaffContainer title="Coaching Staff">
        <div className="text-center text-error-90">Error loading program staff information.</div>
    </ProgramStaffContainer>
);

export function ProgramStaff(props: Props) {
    const { name, authenticated } = props;
    const { FEATURES } = useRouteData(RouteIds.ROOT);
    const location = useLocation();
    const returnTo = `${location.pathname}${location.search}`;
    const coaches = (props.coaches ?? [])
        .filter(c => !c.attrs.name.includes("has been removed"))
        .sort(orderByPosition);
    if (coaches.length <= 0) return <LeadFormCTA name={name} returnTo={returnTo} />;
    const title = "Coaching Staff";
    const testId = "program-staff-modal-content";
    const limit = FEATURES.BETAExperience ? 3 : 5;
    return (
        <Modal>
            <ProgramStaffContainer title={title}>
                <div>
                    <Table
                        coaches={coaches.slice(0, limit)}
                        withBlur={!authenticated}
                        isBeta={FEATURES.BETAExperience}
                    />
                    {coaches.length > limit && (
                        <ModalTrigger
                            className="font-metadata-medium p-3 text-blue-80"
                            aria-controls={testId}
                        >
                            Show More
                        </ModalTrigger>
                    )}
                </div>
                {FEATURES.BETAExperience ? (
                    <BetaLeadFormCTA authenticated={authenticated} returnTo={returnTo} />
                ) : (
                    <LeadFormCTA name={name} returnTo={returnTo} />
                )}
            </ProgramStaffContainer>
            <ModalContent className="size-full" data-testid={testId}>
                <ModalDescription>View Program Staff</ModalDescription>
                <ModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                </ModalHeader>
                <Table
                    coaches={coaches}
                    withBlur={!authenticated}
                    isBeta={FEATURES.BETAExperience}
                />
            </ModalContent>
        </Modal>
    );
}
